import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Label, Textarea, DateInput } from '../../../components/ui';
import { useFlexibleList } from '../../../hooks/useSWR';
import { PlusIcon, TrashIcon, ChevronDownIcon } from '../../../components/ui/Icons';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * RiskAssessmentActionIntegration - Component for managing risk assessment actions within the risk assessment form
 * Similar to ComplaintActionTakenIntegration but for RiskAssessmentAction
 */
const RiskAssessmentActionIntegration = ({ 
  riskAssessmentId,
  complaintId, // Accept complaintId as alias (FormDrawer passes this)
  formData,
  setFormData,
  config,
  isEditMode = false
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [localActions, setLocalActions] = useState([]);

  // Use riskAssessmentId, complaintId (alias), or formData.id
  const actualRiskAssessmentId = riskAssessmentId || complaintId || formData?.id;
  
  //console.log('🔍 RiskAssessmentActionIntegration - Props:', {
  //  riskAssessmentId,
  //  complaintId,
  //  formDataId: formData?.id,
  //  actualRiskAssessmentId,
  //  isEditMode,
  //  hasFormDataActions: !!formData?.actions,
  //  hasFormDataRiskAssessmentActions: !!formData?.riskAssessmentActions,
  //  formDataKeys: formData ? Object.keys(formData) : []
  //});

  // Fetch existing actions if riskAssessmentId exists
  const { data: existingRiskAssessment = null } = useFlexibleList(
    actualRiskAssessmentId && isEditMode ? `/risk-assessment/${actualRiskAssessmentId}` : null
  );

  // Track if we've initialized to prevent infinite loops
  const isInitializedRef = React.useRef(false);
  const prevRiskAssessmentIdRef = React.useRef(actualRiskAssessmentId);
  
  // Use ref to store latest actions for immediate access
  const localActionsRef = React.useRef([]);

  // Helper function to format actions from API response
  const formatActions = (actions) => {
    if (!actions || !Array.isArray(actions)) return [];
    return actions.map(action => ({
      id: action.id,
      description: action.description || '',
      startedAt: action.startedAt || null,
      dueDate: action.dueDate || null,
      closedAt: action.closedAt || null,
    }));
  };

  // Initialize actions only once when component mounts or riskAssessmentId changes
  useEffect(() => {
    // Reset initialization flag when riskAssessmentId changes
    if (prevRiskAssessmentIdRef.current !== actualRiskAssessmentId) {
      isInitializedRef.current = false;
      prevRiskAssessmentIdRef.current = actualRiskAssessmentId;
    }

    // Only initialize if we haven't already done so
    if (isInitializedRef.current) return;

    // Priority: 
    // 1. formData.riskAssessmentActions (from formData when editing)
    // 2. existingRiskAssessment.riskAssessmentActions (from API fetch)
    // 3. formData.actions (fallback)
    // 4. empty array

    let actionsToLoad = null;

    // Check formData.riskAssessmentActions first (most likely to be populated when editing)
    if (formData?.riskAssessmentActions && Array.isArray(formData.riskAssessmentActions) && formData.riskAssessmentActions.length > 0) {
      console.log('📦 Loading actions from formData.riskAssessmentActions:', formData.riskAssessmentActions);
      actionsToLoad = formatActions(formData.riskAssessmentActions);
    }
    // Check existingRiskAssessment from API fetch
    else if (existingRiskAssessment?.riskAssessmentActions && Array.isArray(existingRiskAssessment.riskAssessmentActions) && existingRiskAssessment.riskAssessmentActions.length > 0) {
      console.log('📦 Loading actions from existingRiskAssessment.riskAssessmentActions:', existingRiskAssessment.riskAssessmentActions);
      actionsToLoad = formatActions(existingRiskAssessment.riskAssessmentActions);
    }
    // Check formData.actions as fallback
    else if (formData?.actions && Array.isArray(formData.actions) && formData.actions.length > 0) {
      console.log('📦 Loading actions from formData.actions:', formData.actions);
      actionsToLoad = formatActions(formData.actions);
    }

    if (actionsToLoad && actionsToLoad.length > 0) {
      setLocalActions(actionsToLoad);
      console.log('✅ Loaded actions:', actionsToLoad);
      // Auto-expand when actions are loaded
      setIsExpanded(true);
    } else {
      console.log('📭 No actions found, initializing empty array');
      setLocalActions([]);
    }
    
    isInitializedRef.current = true;
  }, [actualRiskAssessmentId, isEditMode, existingRiskAssessment, formData?.riskAssessmentActions, formData?.actions]);
  
  // Also log when existingRiskAssessment changes
  useEffect(() => {
    if (existingRiskAssessment) {
      //console.log('📥 existingRiskAssessment received:', {
      //  id: existingRiskAssessment.id,
      //  hasActions: !!existingRiskAssessment.riskAssessmentActions,
      //  actionsCount: existingRiskAssessment.riskAssessmentActions?.length || 0,
      //  actions: existingRiskAssessment.riskAssessmentActions
      //});
    }
  }, [existingRiskAssessment]);

  // Update ref whenever localActions changes
  useEffect(() => {
    localActionsRef.current = localActions;
  }, [localActions]);

  // Sync localActions to formData (but only if we've initialized)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Sync immediately without timeout to ensure data is available when form is submitted
    const syncToFormData = () => {
      setFormData(prev => {
        // Only update if the data actually changed to prevent unnecessary re-renders
        const currentActions = prev.actions || [];
        const currentActionsStr = JSON.stringify(currentActions);
        const localActionsStr = JSON.stringify(localActions);
        
        if (currentActionsStr === localActionsStr) {
          return prev;
        }
        
        console.log('🔄 Syncing localActions to formData:', localActions);
        
        return {
          ...prev,
          actions: localActions
        };
      });
    };
    
    syncToFormData();
  }, [localActions, setFormData]);

  const addAction = () => {
    const newAction = {
      id: `temp-${Date.now()}`, // Temporary ID for new actions
      description: '',
      startedAt: null,
      dueDate: null,
      closedAt: null,
      isNew: true // Flag to identify new actions
    };
    setLocalActions(prev => {
      const updated = [...prev, newAction];
      console.log('🔄 Adding action:', updated);
      // Update ref immediately for consistency
      localActionsRef.current = updated;
      return updated;
    });
    setIsExpanded(true); // Auto-expand when adding new action
  };

  const removeAction = (index) => {
    setLocalActions(prev => {
      const newActions = prev.filter((_, i) => i !== index);
      console.log('🔄 Removing action:', newActions);
      // Update ref immediately for consistency
      localActionsRef.current = newActions;
      return newActions;
    });
  };

  const updateAction = (index, field, value) => {
    setLocalActions(prev => {
      const newActions = [...prev];
      newActions[index] = {
        ...newActions[index],
        [field]: value
      };
      console.log('🔄 Updating action:', { index, field, value, newActions });
      // Update ref immediately for consistency
      localActionsRef.current = newActions;
      return newActions;
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  return (
    <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">⚡</span>
          <span className="font-semibold">Actions</span>
          {localActions.length > 0 && (
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              theme.colors.primary,
              theme.text.inverse
            )}>
              {localActions.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className={cn("p-4 border-t", theme.border.light)}>
          {localActions.length === 0 ? (
            <div className={cn("text-center py-8", theme.text.secondary)}>
              <p className="mb-4">No actions added yet.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAction}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add First Action
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {localActions.map((action, index) => (
                <div
                  key={action.id || index}
                  className={cn(
                    "p-4 rounded-lg border space-y-4",
                    theme.border.subtle,
                    theme.colors.backgroundAlt
                  )}
                >
                  {/* Action Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm font-medium", theme.text.secondary)}>
                        Action #{index + 1}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeAction(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Action Fields - Responsive Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Description - Full Width */}
                    <div className="sm:col-span-3">
                      <Label htmlFor={`description-${index}`} required>
                        Description
                      </Label>
                      <Textarea
                        id={`description-${index}`}
                        value={action.description || ''}
                        onChange={(e) => updateAction(index, 'description', e.target.value)}
                        placeholder="Enter action description"
                        rows={2}
                        required
                      />
                    </div>

                    {/* Started At */}
                    <div className="sm:col-span-1">
                      <Label htmlFor={`startedAt-${index}`}>
                        Started At
                      </Label>
                      <DateInput
                        id={`startedAt-${index}`}
                        value={formatDateForInput(action.startedAt)}
                        onChange={(e) => {
                          const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                          updateAction(index, 'startedAt', dateValue);
                        }}
                      />
                    </div>

                    {/* Due Date */}
                    <div className="sm:col-span-1">
                      <Label htmlFor={`dueDate-${index}`}>
                        Due Date
                      </Label>
                      <DateInput
                        id={`dueDate-${index}`}
                        value={formatDateForInput(action.dueDate)}
                        onChange={(e) => {
                          const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                          updateAction(index, 'dueDate', dateValue);
                        }}
                      />
                    </div>

                    {/* Closed At */}
                    <div className="sm:col-span-1">
                      <Label htmlFor={`closedAt-${index}`}>
                        Closed At
                      </Label>
                      <DateInput
                        id={`closedAt-${index}`}
                        value={formatDateForInput(action.closedAt)}
                        onChange={(e) => {
                          const dateValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                          updateAction(index, 'closedAt', dateValue);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Action Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addAction}
                className="w-full"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Another Action
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RiskAssessmentActionIntegration;

